<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import Sortable from 'sortablejs'
import {
  AdminDrawer,
  RrInput,
  RrSelect,
  RrButton,
  RrForm,
  RrFormItem,
  RrCheckbox,
  RrSpace
} from '@xjlv2/admin-layout'
import type { CreateMenuRequest, MenuReorderItem, TenantMenu, TenantMenuGroup, UpdateMenuRequest } from '../../types'

type DraftMode = 'create' | 'edit'

interface MenuRow extends TenantMenu {
  moduleName: string
  depth: number
  hasChildren: boolean
}

interface MenuModuleSection {
  moduleCode: string
  moduleName: string
  rows: MenuRow[]
}

interface MenuDraft {
  mode: DraftMode
  originalCode: string
  moduleCode: string
  menuCode: string
  parentCode: string
  name: string
  icon: string
  menuType: string
  openMode: string
  path: string
  permission: string
  remoteEntry: string
  exposedModule: string
  enabled: boolean
  hidden: boolean
  sortOrder: number
}

const props = defineProps<{
  groups: TenantMenuGroup[]
  canCreate: boolean
  canUpdate: boolean
  canDelete: boolean
  loading: boolean
}>()

const emit = defineEmits<{
  create: [payload: CreateMenuRequest]
  update: [menuCode: string, payload: UpdateMenuRequest]
  reorder: [updates: MenuReorderItem[]]
  delete: [menuCode: string]
}>()

const showDrawer = ref(false)
const expandedCodes = ref<Set<string>>(new Set())
const draft = ref<MenuDraft>(emptyDraft())
const dragNotice = ref('')
const sectionListRefs = new Map<string, HTMLElement>()
const sortableInstances = new Map<string, Sortable>()
let dragStartOrder: string[] = []

const menuRows = computed(() =>
  props.groups.flatMap((group) =>
    group.menus.map((menu) => ({
      ...menu,
      moduleName: group.moduleName
    }))
  )
)

const rowByCode = computed(() => new Map(menuRows.value.map((menu) => [menu.menuCode, menu])))

const childCodesByParent = computed(() => {
  const children = new Map<string, string[]>()
  for (const menu of menuRows.value) {
    const parentCode = menu.parentCode || ''
    const items = children.get(parentCode) ?? []
    items.push(menu.menuCode)
    children.set(parentCode, items)
  }
  return children
})

const flattenedRows = computed<MenuRow[]>(() => {
  const rows: MenuRow[] = []
  for (const group of props.groups) {
    const groupMenus = group.menus
      .map((menu) => ({
        ...menu,
        moduleName: group.moduleName
      }))
      .sort(compareMenus)
    const groupCodes = new Set(groupMenus.map((menu) => menu.menuCode))
    const roots = groupMenus.filter((menu) => !menu.parentCode || !groupCodes.has(menu.parentCode))
    roots.forEach((menu) => appendMenuRow(menu, groupMenus, rows, 0))
  }
  return rows
})

const menuSections = computed<MenuModuleSection[]>(() =>
  props.groups
    .map((group) => ({
      moduleCode: group.moduleCode,
      moduleName: group.moduleName,
      rows: flattenedRows.value.filter((menu) => menu.moduleCode === group.moduleCode)
    }))
    .filter((section) => section.rows.length > 0)
)

const flattenedRowByCode = computed(() => new Map(flattenedRows.value.map((menu) => [menu.menuCode, menu])))

const parentOptions = computed(() =>
  menuRows.value
    .filter((menu) => draft.value.mode === 'create' || menu.menuCode !== draft.value.originalCode)
    .filter((menu) => menu.moduleCode === draft.value.moduleCode)
    .filter((menu) => draft.value.mode === 'create' || !isDescendant(menu.menuCode, draft.value.originalCode))
    .sort(compareMenus)
)

watch(
  () => props.groups,
  () => {
    expandedCodes.value = new Set(menuRows.value.map((menu) => menu.menuCode))
  },
  { immediate: true, deep: true }
)

watch(
  [menuSections, () => props.canUpdate, () => props.loading],
  async () => {
    await nextTick()
    syncSortables()
  },
  { immediate: true, deep: true }
)

onBeforeUnmount(() => {
  destroySortables()
})

function emptyDraft(seed?: Partial<MenuDraft>): MenuDraft {
  return {
    mode: 'create',
    originalCode: '',
    moduleCode: props.groups[0]?.moduleCode ?? 'system',
    menuCode: '',
    parentCode: '',
    name: '新菜单',
    icon: 'menu',
    menuType: 'MENU',
    openMode: 'INTERNAL',
    path: '',
    permission: '',
    remoteEntry: '',
    exposedModule: '',
    enabled: true,
    hidden: false,
    sortOrder: 100,
    ...seed
  }
}

function compareMenus(left: Pick<TenantMenu, 'sortOrder' | 'menuCode'>, right: Pick<TenantMenu, 'sortOrder' | 'menuCode'>) {
  return left.sortOrder - right.sortOrder || left.menuCode.localeCompare(right.menuCode)
}

function appendMenuRow(menu: TenantMenu & { moduleName: string }, groupMenus: Array<TenantMenu & { moduleName: string }>, rows: MenuRow[], depth: number) {
  const children = groupMenus.filter((item) => item.parentCode === menu.menuCode).sort(compareMenus)
  rows.push({
    ...menu,
    depth,
    hasChildren: children.length > 0
  })
  if (!expandedCodes.value.has(menu.menuCode)) return
  children.forEach((child) => appendMenuRow(child, groupMenus, rows, depth + 1))
}

function isDescendant(candidateCode: string, ancestorCode: string) {
  let cursor = rowByCode.value.get(candidateCode)?.parentCode || ''
  const visited = new Set<string>()
  while (cursor && !visited.has(cursor)) {
    if (cursor === ancestorCode) return true
    visited.add(cursor)
    cursor = rowByCode.value.get(cursor)?.parentCode || ''
  }
  return false
}

function toggleExpanded(menu: MenuRow) {
  if (!menu.hasChildren) return
  const next = new Set(expandedCodes.value)
  if (next.has(menu.menuCode)) {
    next.delete(menu.menuCode)
  } else {
    next.add(menu.menuCode)
  }
  expandedCodes.value = next
}

function openCreateDrawer(parent?: TenantMenu) {
  if (!props.canCreate || props.loading) return
  draft.value = emptyDraft(
    parent
      ? {
          moduleCode: parent.moduleCode,
          parentCode: parent.menuCode,
          menuCode: `${parent.menuCode}.new`,
          sortOrder: parent.sortOrder + 10
        }
      : undefined
  )
  showDrawer.value = true
}

function openEditDrawer(menu: TenantMenu) {
  if (!props.canUpdate || props.loading) return
  draft.value = emptyDraft({
    mode: 'edit',
    originalCode: menu.menuCode,
    moduleCode: menu.moduleCode,
    menuCode: menu.menuCode,
    parentCode: menu.parentCode || '',
    name: menu.name,
    icon: menu.icon || 'menu',
    menuType: menu.menuType || 'MENU',
    openMode: menu.openMode || 'INTERNAL',
    path: menu.path,
    permission: menu.permission,
    remoteEntry: menu.remoteEntry,
    exposedModule: menu.exposedModule,
    enabled: menu.enabled,
    hidden: menu.hidden,
    sortOrder: menu.sortOrder
  })
  showDrawer.value = true
}

function closeDrawer() {
  showDrawer.value = false
  draft.value = emptyDraft()
}

function submitDraft() {
  if (!draft.value.name.trim() || !draft.value.menuCode.trim()) return
  const payload = toPayload()
  if (draft.value.mode === 'create') {
    if (!props.canCreate) return
    emit('create', {
      moduleCode: draft.value.moduleCode,
      menuCode: draft.value.menuCode.trim(),
      ...payload
    })
  } else {
    if (!props.canUpdate) return
    emit('update', draft.value.originalCode, payload)
  }
  closeDrawer()
}

function toPayload(): UpdateMenuRequest {
  return {
    parentCode: draft.value.parentCode || null,
    name: draft.value.name.trim(),
    icon: draft.value.icon.trim(),
    menuType: draft.value.menuType,
    openMode: draft.value.openMode,
    path: draft.value.path.trim(),
    permission: draft.value.permission.trim(),
    remoteEntry: draft.value.remoteEntry.trim(),
    exposedModule: draft.value.exposedModule.trim(),
    enabled: draft.value.enabled,
    hidden: draft.value.hidden,
    sortOrder: Number(draft.value.sortOrder)
  }
}

function deleteMenu(menu: TenantMenu) {
  if (!props.canDelete || props.loading) return
  emit('delete', menu.menuCode)
}

function setSectionListRef(moduleCode: string, element: unknown) {
  if (element instanceof HTMLElement) {
    sectionListRefs.set(moduleCode, element)
    return
  }
  sectionListRefs.delete(moduleCode)
}

function syncSortables() {
  const moduleCodes = new Set(menuSections.value.map((section) => section.moduleCode))
  for (const [moduleCode, sortable] of sortableInstances.entries()) {
    if (!moduleCodes.has(moduleCode) || !sectionListRefs.has(moduleCode)) {
      sortable.destroy()
      sortableInstances.delete(moduleCode)
    }
  }

  for (const section of menuSections.value) {
    const element = sectionListRefs.get(section.moduleCode)
    if (!element) continue
    const disabled = !props.canUpdate || props.loading
    const existing = sortableInstances.get(section.moduleCode)
    if (existing) {
      existing.option('disabled', disabled)
      continue
    }
    sortableInstances.set(
      section.moduleCode,
      Sortable.create(element, {
        animation: 160,
        dataIdAttr: 'data-menu-code',
        disabled,
        draggable: '.menu-draggable-row',
        handle: '.drag-handle',
        ghostClass: 'menu-row-ghost',
        chosenClass: 'menu-row-chosen',
        dragClass: 'menu-row-dragging',
        onStart: (event) => {
          dragNotice.value = ''
          dragStartOrder = Sortable.get(event.from)?.toArray() ?? []
        },
        onEnd: handleDragEnd
      })
    )
  }
}

function destroySortables() {
  for (const sortable of sortableInstances.values()) {
    sortable.destroy()
  }
  sortableInstances.clear()
}

function handleDragEnd(event: Sortable.SortableEvent) {
  if (event.oldIndex === undefined || event.newIndex === undefined || event.oldIndex === event.newIndex) return
  const menuCode = event.item.dataset.menuCode
  const moduleCode = event.item.dataset.moduleCode
  const parentCode = event.item.dataset.parentCode ?? ''
  const depth = Number(event.item.dataset.depth ?? 0)
  const menu = menuCode ? rowByCode.value.get(menuCode) : null
  const sortable = moduleCode ? sortableInstances.get(moduleCode) : null

  if (!menu || !moduleCode || !sortable) {
    revertSort(sortable ?? undefined)
    dragNotice.value = '排序失败，未找到当前菜单数据。'
    return
  }

  if (!isSameLevelDrop(event.item, parentCode, depth)) {
    revertSort(sortable)
    dragNotice.value = '只能在同一模块、同一父级菜单内上下拖动排序。'
    return
  }

  const orderedCodes = Array.from(event.from.querySelectorAll<HTMLElement>('.menu-draggable-row'))
    .map((element) => element.dataset.menuCode ?? '')
    .filter(Boolean)
  const nextSiblingCodes = orderedCodes.filter((code) => {
    const row = rowByCode.value.get(code)
    return row?.moduleCode === moduleCode && (row.parentCode || '') === parentCode
  })
  const currentSiblings = menuRows.value
    .filter((row) => row.moduleCode === moduleCode && (row.parentCode || '') === parentCode)
    .sort(compareMenus)
  const currentSiblingCodes = new Set(currentSiblings.map((row) => row.menuCode))
  if (nextSiblingCodes.length !== currentSiblings.length || nextSiblingCodes.some((code) => !currentSiblingCodes.has(code))) {
    revertSort(sortable)
    dragNotice.value = '排序失败，请展开同级菜单后再拖动。'
    return
  }

  const updates = nextSiblingCodes
    .map((code, index) => {
      const row = rowByCode.value.get(code)
      const nextSortOrder = (index + 1) * 10
      if (!row || row.sortOrder === nextSortOrder) return null
      return {
        menuCode: row.menuCode,
        payload: menuToUpdatePayload(row, nextSortOrder)
      }
    })
    .filter((item): item is MenuReorderItem => item !== null)

  if (updates.length === 0) {
    revertSort(sortable)
    return
  }

  emit('reorder', updates)
}

function isSameLevelDrop(item: HTMLElement, parentCode: string, depth: number) {
  const menuCode = item.dataset.menuCode ?? ''
  const previous = rowBoundary(item.previousElementSibling)
  const next = rowBoundary(item.nextElementSibling)
  return isPreviousBoundaryValid(previous, parentCode, depth) && isNextBoundaryValid(next, menuCode, parentCode, depth)
}

function rowBoundary(element: Element | null) {
  if (!(element instanceof HTMLElement) || !element.classList.contains('menu-draggable-row')) return null
  return {
    menuCode: element.dataset.menuCode ?? '',
    parentCode: element.dataset.parentCode ?? '',
    depth: Number(element.dataset.depth ?? 0)
  }
}

function isPreviousBoundaryValid(
  previous: { menuCode: string; parentCode: string; depth: number } | null,
  parentCode: string,
  depth: number
) {
  if (!previous) return depth === 0
  if (previous.depth < depth) return previous.depth === depth - 1 && previous.menuCode === parentCode
  if (previous.depth === depth) return previous.parentCode === parentCode
  const ancestor = ancestorAtDepth(previous.menuCode, depth)
  return ancestor?.parentCode === parentCode
}

function isNextBoundaryValid(
  next: { menuCode: string; parentCode: string; depth: number } | null,
  menuCode: string,
  parentCode: string,
  depth: number
) {
  if (!next) return true
  if (next.depth < depth) return true
  if (next.depth === depth) return next.parentCode === parentCode
  return isDescendant(next.menuCode, menuCode)
}

function ancestorAtDepth(menuCode: string, depth: number) {
  let cursor = flattenedRowByCode.value.get(menuCode)
  const visited = new Set<string>()
  while (cursor && cursor.depth > depth && !visited.has(cursor.menuCode)) {
    visited.add(cursor.menuCode)
    cursor = cursor.parentCode ? flattenedRowByCode.value.get(cursor.parentCode) : undefined
  }
  return cursor?.depth === depth ? cursor : null
}

function revertSort(sortable?: Sortable) {
  if (!sortable || dragStartOrder.length === 0) return
  sortable.sort(dragStartOrder, true)
}

function menuToUpdatePayload(menu: TenantMenu, sortOrder: number): UpdateMenuRequest {
  return {
    parentCode: menu.parentCode || null,
    name: menu.name,
    icon: menu.icon,
    menuType: menu.menuType,
    openMode: menu.openMode,
    path: menu.path,
    permission: menu.permission,
    remoteEntry: menu.remoteEntry,
    exposedModule: menu.exposedModule,
    enabled: menu.enabled,
    hidden: menu.hidden,
    sortOrder
  }
}

function moduleName(moduleCode: string) {
  return props.groups.find((group) => group.moduleCode === moduleCode)?.moduleName ?? moduleCode
}

function typeLabel(menuType: string) {
  const labels: Record<string, string> = {
    MENU: '菜单',
    BUTTON: '按钮',
    IFRAME: 'iframe',
    LINK: '链接'
  }
  return labels[menuType] ?? menuType
}

function openModeLabel(openMode: string) {
  const labels: Record<string, string> = {
    INTERNAL: '内部打开',
    EXTERNAL: '外部打开',
    IFRAME: 'iframe',
    NEW_TAB: '新窗口'
  }
  return labels[openMode] ?? openMode
}

const menuTypeOptions = [
  { label: '菜单', value: 'MENU' },
  { label: '按钮', value: 'BUTTON' },
  { label: 'iframe', value: 'IFRAME' },
  { label: '链接', value: 'LINK' }
]

const openModeOptions = [
  { label: '内部打开', value: 'INTERNAL' },
  { label: '外部打开', value: 'EXTERNAL' },
  { label: 'iframe', value: 'IFRAME' },
  { label: '新窗口', value: 'NEW_TAB' }
]

const moduleSelectOptions = computed(() =>
  props.groups.map((group) => ({
    label: group.moduleName,
    value: group.moduleCode
  }))
)

const parentSelectOptions = computed(() => [
  { label: '顶级菜单', value: '' },
  ...parentOptions.value.map((opt) => ({
    label: `${moduleName(opt.moduleCode)} / ${opt.name}`,
    value: opt.menuCode
  }))
])
</script>

<template>
  <section class="panel span management-list">
    <div class="panel-head">
      <div>
        <p class="eyebrow">Menu Management</p>
        <h2>菜单管理</h2>
      </div>
      <span>{{ menuRows.length }} 个菜单</span>
    </div>

    <div class="menu-action-strip">
      <RrButton type="primary" :disabled="!canCreate || loading" @click="openCreateDrawer()">新增菜单</RrButton>
    </div>

    <p v-if="dragNotice" class="notice menu-sort-notice">{{ dragNotice }}</p>

    <div class="management-table menu-management-table tree-menu-table">
      <div class="management-table-head">
        <span>名称</span>
        <span>图标</span>
        <span>类型</span>
        <span>打开方式</span>
        <span>菜单隐藏</span>
        <span>排序</span>
        <span>路由</span>
        <span>授权标识</span>
        <span>操作</span>
      </div>
      <template v-for="section in menuSections" :key="section.moduleCode">
        <div class="management-table-row menu-module-row">
          <span>
            <strong>{{ section.moduleName }}</strong>
            <small>{{ section.moduleCode }} · {{ section.rows.length }} 个菜单</small>
          </span>
        </div>
        <div
          :ref="(element) => setSectionListRef(section.moduleCode, element)"
          class="menu-sortable-list"
          :data-module-code="section.moduleCode"
        >
          <div
            v-for="menu in section.rows"
            :key="menu.menuCode"
            class="management-table-row menu-draggable-row"
            :data-menu-code="menu.menuCode"
            :data-module-code="menu.moduleCode"
            :data-parent-code="menu.parentCode || ''"
            :data-depth="menu.depth"
          >
            <span class="tree-menu-name" :style="{ '--tree-depth': String(menu.depth) }">
              <button
                type="button"
                class="drag-handle"
                :disabled="!canUpdate || loading"
                aria-label="拖动排序"
                title="拖动排序"
              >
                <span aria-hidden="true"></span>
              </button>
              <button
                type="button"
                class="tree-toggle"
                :class="{ expanded: expandedCodes.has(menu.menuCode), invisible: !menu.hasChildren }"
                :aria-label="expandedCodes.has(menu.menuCode) ? '收起菜单' : '展开菜单'"
                @click="toggleExpanded(menu)"
              ></button>
              <span class="tree-menu-label">
                <strong>{{ menu.name }}</strong>
                <small>{{ menu.menuCode }}</small>
              </span>
            </span>
            <span class="menu-icon-cell">{{ menu.icon || '-' }}</span>
            <span><span class="status-pill">{{ typeLabel(menu.menuType) }}</span></span>
            <span><span class="muted-chip">{{ openModeLabel(menu.openMode) }}</span></span>
            <span>
              <span class="status-pill" :class="{ muted: menu.hidden }">{{ menu.hidden ? '隐藏' : '显示' }}</span>
            </span>
            <span>{{ menu.sortOrder }}</span>
            <span>{{ menu.path || '-' }}</span>
            <span>{{ menu.permission || '-' }}</span>
            <span class="row-actions compact-actions">
              <RrButton type="link" :disabled="!canCreate || loading" @click="openCreateDrawer(menu)">
                新增
              </RrButton>
              <RrButton type="link" :disabled="!canUpdate || loading" @click="openEditDrawer(menu)">
                修改
              </RrButton>
              <RrButton type="link" danger :disabled="!canDelete || loading" @click="deleteMenu(menu)">
                删除
              </RrButton>
            </span>
          </div>
        </div>
      </template>
    </div>

    <p v-if="flattenedRows.length === 0" class="empty-state">暂无菜单。</p>

    <AdminDrawer
      v-model:open="showDrawer"
      :title="draft.mode === 'create' ? '新增菜单' : '修改菜单'"
      :description="draft.mode === 'create' ? (canCreate ? '可写' : '只读') : (canUpdate ? '可写' : '只读')"
      :width="600"
      @close="closeDrawer"
    >
      <RrForm
        layout="vertical"
        :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)"
        @submit.prevent="submitDraft"
      >
        <div class="form-grid inline-form-grid">
          <RrFormItem label="所属模块">
            <RrSelect v-model:value="draft.moduleCode" :disabled="draft.mode === 'edit'" :options="moduleSelectOptions" />
          </RrFormItem>
          <RrFormItem label="上级菜单">
            <RrSelect v-model:value="draft.parentCode" :options="parentSelectOptions" />
          </RrFormItem>
          <RrFormItem label="菜单编码" required>
            <RrInput v-model:value.trim="draft.menuCode" autocomplete="off" :disabled="draft.mode === 'edit'" />
          </RrFormItem>
          <RrFormItem label="名称" required>
            <RrInput v-model:value.trim="draft.name" autocomplete="off" />
          </RrFormItem>
          <RrFormItem label="图标">
            <RrInput v-model:value.trim="draft.icon" autocomplete="off" placeholder="menu" />
          </RrFormItem>
          <RrFormItem label="类型">
            <RrSelect v-model:value="draft.menuType" :options="menuTypeOptions" />
          </RrFormItem>
          <RrFormItem label="打开方式">
            <RrSelect v-model:value="draft.openMode" :options="openModeOptions" />
          </RrFormItem>
          <RrFormItem label="路由">
            <RrInput v-model:value.trim="draft.path" autocomplete="off" placeholder="/permission-management/menus" />
          </RrFormItem>
          <RrFormItem label="授权标识">
            <RrInput v-model:value.trim="draft.permission" autocomplete="off" placeholder="system:menu:read" />
          </RrFormItem>
          <RrFormItem label="Remote Entry">
            <RrInput v-model:value.trim="draft.remoteEntry" autocomplete="off" placeholder="/mfe/demo/remoteEntry.js" />
          </RrFormItem>
          <RrFormItem label="暴露模块">
            <RrInput v-model:value.trim="draft.exposedModule" autocomplete="off" placeholder="./App" />
          </RrFormItem>
          <RrFormItem label="排序">
            <RrInput v-model:value.number="draft.sortOrder" type="number" min="0" max="9999" />
          </RrFormItem>
        </div>

        <div style="display: flex; gap: 16px; margin-top: 16px;">
          <RrCheckbox v-model:checked="draft.enabled">
            {{ draft.enabled ? '启用' : '停用' }}
          </RrCheckbox>
          <RrCheckbox v-model:checked="draft.hidden">
            {{ draft.hidden ? '隐藏' : '显示' }}
          </RrCheckbox>
        </div>
      </RrForm>

      <template #footer>
        <RrSpace>
          <RrButton @click="closeDrawer">取消</RrButton>
          <RrButton
            type="primary"
            :disabled="loading || (draft.mode === 'create' ? !canCreate : !canUpdate)"
            @click="submitDraft"
          >
            {{ draft.mode === 'create' ? '创建菜单' : '保存菜单' }}
          </RrButton>
        </RrSpace>
      </template>
    </AdminDrawer>
  </section>
</template>
